// src/components/Branches-Drawer/BranchesDrawer.tsx
//
// Public "Branches" drawer. Mirrors the Browse drawer (SideDrawer) styling and
// open/close behaviour, but lists warehouses flagged for website display.
import React, { useState, useEffect } from 'react';
import { BranchService, Branch, WeekDay, WEEK_DAYS } from '../../Services/branchService';
import '../Side-Drawer/SideDrawer.css';
import './BranchesDrawer.css';

interface BranchesDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const DAY_LABELS: Record<WeekDay, string> = {
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
  sunday: 'Sunday',
};

// JS getDay(): 0 = Sunday … 6 = Saturday
const JS_DAY_TO_WEEKDAY: WeekDay[] = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
];

const BranchesDrawer: React.FC<BranchesDrawerProps> = ({ isOpen, onClose }) => {
  const [visible, setVisible] = useState(false);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [loaded, setLoaded] = useState(false);
  // Which branches have their Opening Hours section expanded (collapsed by default).
  const [expandedHours, setExpandedHours] = useState<Record<string, boolean>>({});

  const todayWeekday = JS_DAY_TO_WEEKDAY[new Date().getDay()];

  const toggleHours = (branchId: string) =>
    setExpandedHours((prev) => ({ ...prev, [branchId]: !prev[branchId] }));

  // Body scroll lock — identical approach to SideDrawer.
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setVisible(true);
    } else {
      document.body.style.overflow = '';
      const timer = setTimeout(() => setVisible(false), 350);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Lazy-load branches the first time the drawer is opened.
  useEffect(() => {
    if (isOpen && !loaded) {
      setLoading(true);
      setError('');
      BranchService.getPublicBranches()
        .then((data) => {
          setBranches(data);
          setLoaded(true);
        })
        .catch(() => setError('Unable to load branches right now.'))
        .finally(() => setLoading(false));
    }
  }, [isOpen, loaded]);

  const renderHours = (branch: Branch) => {
    const oh = branch.operatingHours;
    if (!oh) return null;

    const isExpanded = !!expandedHours[branch.id];

    // Today's hours — shown as a summary line when the section is collapsed.
    const todayDh = oh.days?.[todayWeekday];
    const todayIsOpen = todayDh ? todayDh.isOpen : true;
    const todayOpen = todayDh?.open || oh.open || '09:00';
    const todayClose = todayDh?.close || oh.close || '18:00';

    return (
      <>
        <div className="branch-card__divider" />
        <button
          type="button"
          className="branch-hours__title branch-hours__toggle"
          onClick={() => toggleHours(branch.id)}
          aria-expanded={isExpanded}
          aria-controls={`branch-hours-${branch.id}`}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
          <span className="branch-hours__title-text">Opening Hours</span>
          {!isExpanded && (
            <span className={`branch-hours__today-summary${todayIsOpen ? '' : ' branch-hours__time--closed'}`}>
              {todayIsOpen ? `Today ${todayOpen} – ${todayClose}` : 'Closed today'}
            </span>
          )}
          <svg
            className={`branch-hours__chevron${isExpanded ? ' branch-hours__chevron--open' : ''}`}
            width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
        {isExpanded && (
          <div className="branch-hours" id={`branch-hours-${branch.id}`}>
            {WEEK_DAYS.map((day) => {
              const dh = oh.days?.[day];
              const isOpenDay = dh ? dh.isOpen : true;
              const open = dh?.open || oh.open || '09:00';
              const close = dh?.close || oh.close || '18:00';
              const isToday = day === todayWeekday;
              return (
                <div
                  key={day}
                  className={`branch-hours__row${isToday ? ' branch-hours__row--today' : ''}`}
                >
                  <span className="branch-hours__day">{DAY_LABELS[day]}</span>
                  <span className={`branch-hours__time${isOpenDay ? '' : ' branch-hours__time--closed'}`}>
                    {isOpenDay ? `${open} – ${close}` : 'Closed'}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </>
    );
  };

  if (!visible && !isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className={`drawer-backdrop ${isOpen ? 'drawer-backdrop--visible' : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer Panel */}
      <div
        className={`side-drawer ${isOpen ? 'side-drawer--open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Branches"
      >
        {/* Header */}
        <div className="side-drawer__header">
          <span className="side-drawer__title">Our Branches</span>
          <button className="side-drawer__close" onClick={onClose} aria-label="Close branches">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="side-drawer__content">
          <div className="side-drawer__panel side-drawer__panel--active" style={{ position: 'absolute', inset: 0, padding: 0 }}>
            {loading ? (
              <div className="side-drawer__loading">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="side-drawer__skeleton" style={{ height: 150 }} />
                ))}
              </div>
            ) : error ? (
              <p className="side-drawer__empty">{error}</p>
            ) : branches.length === 0 ? (
              <p className="side-drawer__empty">No branches available at the moment.</p>
            ) : (
              <ul className="branch-list">
                {branches.map((branch) => (
                  <li key={branch.id} className="branch-card">
                    <div className="branch-card__head">
                      <span className="branch-card__badge">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                          strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M3 21h18" />
                          <path d="M5 21V7l7-4 7 4v14" />
                          <path d="M9 21v-6h6v6" />
                        </svg>
                      </span>
                      <div>
                        <p className="branch-card__name">{branch.name}</p>
                      </div>
                    </div>

                    {/* Address */}
                    <div className="branch-card__row">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                        <circle cx="12" cy="10" r="3" />
                      </svg>
                      <span>
                        {[branch.address.street, branch.address.city, branch.address.state, branch.address.country]
                          .filter(Boolean)
                          .join(', ')}
                        {branch.address.zipCode ? ` ${branch.address.zipCode}` : ''}
                      </span>
                    </div>

                    {/* Phone */}
                    {branch.phone && (
                      <div className="branch-card__row">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                          strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                        </svg>
                        <a href={`tel:${branch.phone}`}>{branch.phone}</a>
                      </div>
                    )}

                    {/* Email */}
                    {branch.email && (
                      <div className="branch-card__row">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                          strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                          <polyline points="22,6 12,13 2,6" />
                        </svg>
                        <a href={`mailto:${branch.email}`}>{branch.email}</a>
                      </div>
                    )}

                    {renderHours(branch)}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default BranchesDrawer;
